// Suppress iOS Safari's double-tap-to-zoom by preventDefault-ing the second
// touchend when two touchend events fire within ~250ms of each other on the
// same document.
//
// Threshold: iOS Safari's double-tap-zoom window is ~300ms historically (not
// documented by Apple, but the common figure across Stack Overflow / WebKit
// bug reports). We sit BELOW that at 250ms so single rapid taps with a gap
// of 250-300ms still register normally — at the cost of letting through the
// occasional slower double-tap that does still trigger the zoom. A 2026-05-22
// session with 350ms caught more zooms but made every tap feel laggier; this
// 250ms revision is a deliberate "snappier vs. zoom-incidence" trade.
//
// Side effects:
//   - Two taps with <250ms gap produce no second click. Game flicks use
//     pointerdown/pointerup so they are unaffected.
//   - Word-bar tap-to-explain still works on every deliberate tap; only the
//     SECOND tap of a quick double-tap is suppressed.
//   - Pinch-zoom is untouched (pinch uses two simultaneous touches, not two
//     consecutive touchend events).

const DOUBLE_TAP_WINDOW_MS = 250

let lastTouchEnd = 0

document.addEventListener(
  'touchend',
  (e: TouchEvent): void => {
    const now = Date.now()
    if (now - lastTouchEnd <= DOUBLE_TAP_WINDOW_MS) {
      e.preventDefault()
    }
    lastTouchEnd = now
  },
  { passive: false },
)

// Reset the double-tap latch. Called by the resume reconciler's onResume hook:
// after a long background gap, the stale `lastTouchEnd` timestamp would be far
// in the past, so on its own it can't suppress the first tap — but if the
// player happened to background mid-double-tap, the residual timestamp could
// suppress the very first deliberate tap on resume. Zeroing it on every resume
// guarantees the first post-resume tap always registers.
export function resetDoubleTapState(): void {
  lastTouchEnd = 0
}
